import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { get } from 'lodash';

import { LocaleSelect } from '../utils/locale-provider';

import { Battery } from '../atoms/icons/battery';
import { ParentPaper } from '../atoms/parent-paper';
import { ControlBox } from '../atoms/control-box';

import { SliderBox } from '../molecules/slider-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { InputBox } from '../molecules/input-box';

import { Group } from '../organisms/group';

import { FormattedValue } from '../store/formatted-value';

import { useSelector } from '../store/utils';
import { eanSchema } from '../utils/zod-utils';
import { ReadabilityModeSelect } from '../organisms/settings/readability-mode-select';
import { HelpButton } from '../atoms/help-button';
import { Button, Paper } from '@mui/material';
import { HDOSettingsUser } from '../organisms/settings/hdo-settings-user';
import { Inverter } from '../shared';

import { SettingsService } from '../templates/settings/settings-service';
import { SettingsNotifications } from '../templates/settings/settings-notifications';
import { SettingsSpot } from '../templates/settings/settings-spot';
import { SettingsOverflowInfo } from '../templates/settings/boxes/settings-overflow-info';
import { PremiumBanner } from '../templates/premium/premium-banner';
import { DarkModeToggle } from '../atoms/dark-mode-toggle';

const Settings = () => {
  const { state } = useLocation();

  const snVisibility = useSelector(
    (s) => get(s.variables, 'SERIAL_NUMBER') != ''
  );

  const fronius = useSelector((s) => {
    const inverterConfig = s.variables.INVERTER_CONFIG;
    const inverterConfigArray = Object.values(inverterConfig ?? {});
    return (
      inverterConfigArray[0]?.type === Inverter.Fronius ||
      inverterConfigArray[0]?.type === Inverter.FroniusVerto
    );
  });

  const premium = useSelector((s) => get(s.variables, 'PREMIUM'));

  const multipleWallboxes = useSelector(
    (s) =>
      get(s.variables, 'WB_TYPE') === 'next' &&
      get(s.variables, 'WB_WALLBOX_CONNECTED_COUNT') > 1
  );

  const wallboxKeys = useSelector((s) => {
    const config = get(s.variables, 'WALLBOX_CONFIG') || {};
    return Object.keys(config);
  });

  useEffect(() => {
    if (get(state, 'scroll') === 'service') {
      const element = document.getElementById('service');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [state]);

  return (
    <>
      <PremiumBanner variant="settings" />

      <Group
        title={
          <FormattedMessage
            id="settings.docs"
            defaultMessage="Uživatelský manuál"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <ControlBox
            title={
              <FormattedMessage
                id="settings.docs.user_wiki.title"
                defaultMessage="Uživatelská wiki"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.docs.user_wiki.subtitle"
                defaultMessage="Návody, postupy a další informace"
              />
            }
            fullWidth
            control={
              <Button
                variant="contained"
                href="https://infigy.cz/app-redirect/wiki"
                target="_blank"
              >
                <FormattedMessage
                  id="settings.docs.user_wiki.button"
                  defaultMessage="Přejít na wiki"
                />
              </Button>
            }
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="settings.photovoltaics.settings"
            defaultMessage="Baterie"
          />
        }
        layout="list"
        visibility={{ variableIds: ['PV_BATTERY', 'PV_BATTERY_CONTROL'] }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.photovoltaics_auto_settings"
                defaultMessage="Automatické řízení nabití"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.photovoltaics_auto_settings.subtitle"
                defaultMessage="Automatická regulace nabití podle ročního období"
              />
            }
            switchVariableId="SOC_AUTO"
            fullWidth
          />

          <ValueBox
            type="row"
            title={
              <>
                <FormattedMessage
                  id="settings.photovoltaics.settings.soc_actual"
                  defaultMessage="Vypočtené minimální nabití"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.soc_actual"
                      defaultMessage="Vypočtené minimální nabití"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.soc_actual.help"
                      defaultMessage="Tato hodnota je automaticky vypočtena na základě aktuálního stavu. Buď se řídí dle automatického řízení nabití podle ročního období, nebo dle manuálního nastavení minimálního nabití s DS a bez DS. Pokud je zde hodnota 100%, tak je vybíjení baterie zakázané, např. dle spotového obchodování."
                    />
                  }
                />
              </>
            }
            value={
              <FormattedValue
                variableId="PV_SOC_MIN_FINAL"
                opts={{
                  style: 'percent',
                  maximumFractionDigits: 0,
                }}
              />
            }
            icon={<Battery />}
          />

          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="settings.photovoltaics.settings.soc_grid"
                  defaultMessage="Minimální nabití s DS"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.soc_grid"
                      defaultMessage="Minimální nabití s DS"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.soc_grid.help"
                      defaultMessage="Touto hodnotou je možné řídit minimální nabití baterie v % SOC při dostupné distribuční síti. U Solax v režimu Backup je minimální nabití baterie 15%."
                    />
                  }
                />
              </>
            }
            variableId="PV_SOC_SET_MIN"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={fronius ? 5 : 10}
          />
          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="settings.photovoltaics.settings.temp_ek"
                  defaultMessage="Minimální nabití bez DS"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.temp_ek"
                      defaultMessage="Minimální nabití bez DS"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="settings.photovoltaics.settings.temp_ek.help"
                      defaultMessage="Touto hodnotou je možné řídit minimální nabití baterie v % SOC při výpadku distribuční sítě."
                    />
                  }
                />
              </>
            }
            variableId="PV_SOC_SET_UTILITY_LOST"
            visibility={{ variableId: 'PV_SOC_SET_UTILITY_LOST_V' }}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={fronius ? 5 : 10}
            max={25}
          />
        </ParentPaper>
      </Group>
      <Group
        title={
          <FormattedMessage
            id="settings.heatpump"
            defaultMessage="Tepelné čerpadlo"
          />
        }
        layout="list"
        visibility={{ variableId: 'HP_ENABLED' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.priority"
                defaultMessage="Tepelné čerpadlo má vyšší prioritu, než baterie"
              />
            }
            switchVariableId="HP_PRIORITY"
            fullWidth
            visibility={{ variableId: 'HP_ENABLED' }}
          />
          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="settings.heatpump.HpBatteryPriority"
                  defaultMessage="Procento nabítí baterie od kterého se zvýší výkon tepelného čerpadla"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="settings.heatpump.HpBatteryPriority"
                      defaultMessage="Procento nabítí baterie od kterého se zvýší výkon tepelného čerpadla"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="settings.heatpump.HpBatteryPriority.help"
                      defaultMessage="Pokud je nabití baterie nižší, než tato hodnota, priorita akumulace energie je plně dostupna baterii. Pokud je nabití baterie vyšší, tepelné čerpadlo začíná přebírat prioritu a spouští ohřev vody z přebytečné energie FVE - část energie jde dále do baterie do jejího dobití."
                    />
                  }
                />
              </>
            }
            variableId="PV_OFFSET_SOC_HP_PRIO"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={30}
            max={97}
            visibility={{
              variableIds: [{ variableId: 'HP_PRIORITY', reversed: true }],
            }}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatpump.priority-wb"
                defaultMessage="Tepelné čerpadlo má vyšší prioritu, než wallbox"
              />
            }
            switchVariableId="WB_HP_PRIORITY"
            fullWidth
            visibility={{ variableId: 'HP_ENABLED' }}
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage id="settings.heatwatter" defaultMessage="Bojler" />
        }
        layout="list"
        visibility={{
          variableIds: ['HW_INSTALL'],
        }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.boiler.prio"
                defaultMessage="Priorita baterie "
              />
            }
            switchVariableId="HW_BATTERY_PRIO"
            subtitle={
              <FormattedMessage
                id="settings.boiler.prio.subtitle"
                defaultMessage="Pokud je ON, nejprve se nabijí baterie a následně bojler"
              />
            }
            fullWidth
            visibility={{ variableIds: ['PV_BATTERY', 'PV_BATTERY_CONTROL'] }}
          />
          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="settings.boiler.HwBatteryPriority"
                  defaultMessage="Procento nabítí baterie od kterého se začne ohřívat bojler"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="settings.boiler.HwBatteryPriority"
                      defaultMessage="Procento nabítí baterie od kterého se začne ohřívat bojler"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="settings.boiler.HwBatteryPriority.help"
                      defaultMessage="Pokud je nabití baterie nižší, než tato hodnota, priorita akumulace energie je plně dostupna baterii. Pokud je nabití baterie vyšší, bojler začíná přebírat prioritu a spouští ohřev vody z přebytečné energie FVE - část energie jde dále do baterie do jejího dobití."
                    />
                  }
                />
              </>
            }
            variableId="PV_OFFSET_SOC_HW_PRIO"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={30}
            max={97}
            visibility={{ variableId: 'HW_BATTERY_PRIO' }}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.heatwatter.legionella"
                defaultMessage="Legionella"
              />
            }
            switchVariableId="HW_LEGIONELLA_ON"
            fullWidth
            visibility={{ variableId: 'HW_TEMP_ENABLED' }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.heatwatter.legionella_temp"
                defaultMessage="Teplota sanace"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.heatwatter.legionella_temp.subtitle"
                defaultMessage="Teplota na kterou se musí minimálně 1x za 14 dní bojler nahřát"
              />
            }
            variableId="HW_LEGIONELLA_TEMP"
            visibility={{ variableId: 'HW_LEGIONELLA_ON' }}
            max={85}
            min={45}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
          />
        </ParentPaper>
      </Group>

      <Group
        title={<FormattedMessage id="settings.plug" defaultMessage="Zásuvky" />}
        layout="list"
        visibility={{ variableId: 'PL_INSTALL' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.statusLed"
                defaultMessage="Signalizační dioda"
              />
            }
            switchVariableId="PL_STATUSLED"
            fullWidth
            visibility={{
              variableIds: [
                'PL_INSTALL',
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
              ],
            }}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.Led"
                defaultMessage="Trvalé podsvícení"
              />
            }
            switchVariableId="PL_LED"
            fullWidth
            visibility={{
              variableIds: [
                'PL_INSTALL',
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
              ],
            }}
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.Led-batt"
                defaultMessage="Podsvícení při nabité baterii"
              />
            }
            switchVariableId="PL_LED_BAT"
            fullWidth
            visibility={{
              variableIds: [
                'PL_INSTALL',
                'PV_BATTERY',
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
              ],
            }}
            subtitle={
              <FormattedMessage
                id="settings.plug.Led-batt.subtitle"
                defaultMessage="Podsvícení aktivní, pokud má baterie alespoň 95%"
              />
            }
          />
          <SwitchBox
            title={
              <FormattedMessage
                id="settings.plug.Led-over"
                defaultMessage="Podsvícení při přetoku"
              />
            }
            switchVariableId="PL_LED_OVER"
            fullWidth
            visibility={{
              variableIds: [
                'PL_INSTALL',
                { variableId: 'PL_SHELLY_HW_PLUG', reversed: true },
              ],
            }}
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.name1"
                defaultMessage="Název zásuvky 1"
              />
            }
            inputVariableId="PL_NAME[0]"
            visibility={{ variableId: 'PL_INSTALL' }}
            label={
              <FormattedMessage
                id="settings.plug.name1.label"
                defaultMessage="Název"
              />
            }
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.name2"
                defaultMessage="Název zásuvky 2"
              />
            }
            inputVariableId="PL_NAME[1]"
            visibility={{ variableId: 'PL_INSTALL_I[1]' }}
            label={
              <FormattedMessage
                id="settings.plug.name2_label"
                defaultMessage="Název"
              />
            }
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="settings.plug.name3"
                defaultMessage="Název zásuvky 3"
              />
            }
            inputVariableId="PL_NAME[2]"
            visibility={{ variableId: 'PL_INSTALL_I[2]' }}
            label={
              <FormattedMessage
                id="settings.plug.name3.label"
                defaultMessage="Název"
              />
            }
            fullWidth
          />
        </ParentPaper>
      </Group>
      {multipleWallboxes && (
        <Group
          title={
            <FormattedMessage id="settings.wallbox" defaultMessage="Wallbox" />
          }
          layout="list"
        >
          <ParentPaper>
            {wallboxKeys.map((wallboxKey, index) => (
              <InputBox
                key={wallboxKey}
                title={
                  <FormattedMessage
                    id="settings.wallbox.name"
                    defaultMessage="Název wallboxu {number}"
                    values={{ number: index + 1 }}
                  />
                }
                inputVariableId={`WALLBOX_CONFIG.${wallboxKey}.name`}
                fullWidth
              />
            ))}
          </ParentPaper>
        </Group>
      )}
      <SettingsSpot />

      <Group
        title={
          <>
            <FormattedMessage
              id="settings.hdo"
              defaultMessage="Nastavení časů HDO"
            />
            <HelpButton
              variant="dialog"
              title={
                <FormattedMessage
                  id="settings.hdo"
                  defaultMessage="Nastavení časů HDO"
                />
              }
              content={
                <FormattedMessage
                  id="settings.hdo.help"
                  defaultMessage="Nastavení časů HDO pro výpočet cen elektřiny ve vysokém a nízkém tarifu. Níže je možné zadat časy platnosti nízkého tarifu."
                />
              }
            />
          </>
        }
        layout="list"
        collapsible
      >
        <HDOSettingsUser />
      </Group>
      <Group
        title={
          <FormattedMessage id="settings.device" defaultMessage="Zařízení" />
        }
        layout="list"
      >
        <ParentPaper>
          <ControlBox
            title={
              <FormattedMessage
                id="settings.device.license"
                defaultMessage="Licence zařízení"
              />
            }
            fullWidth
            subtitle={<FormattedValue variableId="LICENSE_STATUS" />}
          />
          <ControlBox
            title={
              <FormattedMessage
                id="settings.device.id"
                defaultMessage="ID zařízení"
              />
            }
            fullWidth
            subtitle={<FormattedValue variableId="DEVICE_ID" />}
          />
          {snVisibility && (
            <ControlBox
              title={
                <FormattedMessage
                  id="settings.device.SN"
                  defaultMessage="Sériové číslo"
                />
              }
              fullWidth
              subtitle={<FormattedValue variableId="SERIAL_NUMBER" />}
            />
          )}
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage id="settings.other" defaultMessage="Ostatní" />
        }
        layout="list"
      >
        <ParentPaper>
          <ControlBox
            title={
              <FormattedMessage
                id="settings.other.languate"
                defaultMessage="Jazyk"
              />
            }
            fullWidth
            control={<LocaleSelect />}
          />
          <InputBox
            title={<FormattedMessage id="ean" defaultMessage="EAN" />}
            inputVariableId="EAN_PURCHASE"
            fullWidth
            schema={eanSchema}
            inputWidth={250}
          />
          <ControlBox
            title={
              <FormattedMessage
                id="settings.other.readability_mode"
                defaultMessage="Velikost písma"
              />
            }
            fullWidth
            control={<ReadabilityModeSelect />}
          />
          <ControlBox
            title={
              <FormattedMessage
                id="settings.other.dark_mode"
                defaultMessage="Tmavý režim"
              />
            }
            fullWidth
            control={<DarkModeToggle />}
          />
          <SettingsOverflowInfo />
          {fronius && (
            <ControlBox
              title={
                <FormattedMessage
                  id="settings.other.overflow_fronius"
                  defaultMessage="Maximální přetok do sítě je omezován na straně Fronius střídače"
                />
              }
              fullWidth
              subtitle={
                <FormattedMessage
                  id="settings.other.overflow_fronius_subtitle"
                  defaultMessage="Tato hodnota je stanovena distributorem. Překročení této hodnoty může vést k finančním postihům ze strany distributora."
                />
              }
            />
          )}
        </ParentPaper>
      </Group>
      {premium && <SettingsNotifications />}

      <Group
        title={
          <FormattedMessage id="settings.service" defaultMessage="Servis" />
        }
        layout="list"
        id="service"
      >
        <Paper
          sx={{
            p: 0,
            bgcolor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.text.disabled
                : '#39393D',
            '& .group__title': { color: 'common.white' },
            '& .MuiPaper-root': {
              boxShadow: 0,
            },
          }}
        >
          <SettingsService />
        </Paper>
      </Group>
    </>
  );
};

export default Settings;
